import { normalizeActionedAtTimeToUtc } from "helpers/actionedAtTime";

/**
 * Push to Audit Trail prefills one UTC time box per option from the import
 * clock (actioned_at_time). Confirm sends that box as Actioned at.
 * Team Run does not read this field.
 */

function storedClock(value) {
  if (value == null) return null;
  const text = String(value).trim();
  return text === "" ? null : text;
}

/** Top-level clock, otherwise summary_agg (same object shorthand is copied from). */
export function readRowActionedAtTime(row) {
  return storedClock(
    row?.actioned_at_time ??
      row?.summary_agg?.actioned_at_time ??
      row?.actioned_at ??
      row?.summary_agg?.actioned_at
  );
}

export function readRowCopyShorthand(row) {
  const value = row?.copy_shorthand ?? row?.summary_agg?.copy_shorthand;
  if (value == null) return "";
  return String(value);
}

/**
 * UTC clock parts. Accepts HH:MM, HH:MM:SS, fractional seconds, and a
 * datetime or clock that carries Z, a numeric offset, or a zone abbreviation.
 */
export function parseActionedAtClock(timeString) {
  const utc = normalizeActionedAtTimeToUtc(timeString);
  if (!utc) return null;
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(utc);
  if (!match) return null;
  return {
    hour: Number(match[1]),
    minute: Number(match[2]),
    second: match[3] == null ? 0 : Number(match[3]),
  };
}

export function indexPricerRowsForAuditTrail(nodes) {
  const summaryById = new Map();
  const leafById = new Map();
  (nodes ?? []).forEach((node) => {
    const data = node?.group ? node.aggData : node?.data;
    const summaryId = data?.summary_id ?? data?.summary_reference_id;
    if (summaryId == null) return;
    if (node.level === 0 && !summaryById.has(summaryId)) {
      summaryById.set(summaryId, data);
    }
    if (!node?.group && !leafById.has(summaryId)) {
      leafById.set(summaryId, data);
    }
  });
  return { summaryById, leafById };
}

function summaryIdOf(row) {
  return row?.summary_id ?? row?.summary_reference_id ?? row?.id ?? null;
}

function usableActionedAtTime(row) {
  const time = readRowActionedAtTime(row);
  return parseActionedAtClock(time) ? time : null;
}

/**
 * One plain option per selected summary, grid order when orderedIds is set.
 * Time comes from the summary row, then a leaf, then the selected row.
 */
export function buildPushToAuditTrailOptions(
  selectedRows,
  { summaryById, leafById, orderedIds } = {}
) {
  const seen = new Set();
  const options = [];

  (selectedRows ?? []).forEach((row) => {
    const summaryId = summaryIdOf(row);
    if (summaryId == null || seen.has(summaryId)) return;
    seen.add(summaryId);

    const summary = summaryById?.get(summaryId);
    const leaf = leafById?.get(summaryId);
    const shorthand =
      readRowCopyShorthand(summary) ||
      readRowCopyShorthand(leaf) ||
      readRowCopyShorthand(row);
    const actionedAtTime =
      usableActionedAtTime(summary) ??
      usableActionedAtTime(leaf) ??
      usableActionedAtTime(row);

    options.push({
      summary_id: summaryId,
      copy_shorthand: shorthand,
      actioned_at_time: formatActionedAtClock(actionedAtTime),
    });
  });

  if (!orderedIds?.length) return options;
  const rank = new Map(orderedIds.map((id, index) => [id, index]));
  return [...options].sort(
    (a, b) =>
      (rank.get(a.summary_id) ?? 999999) - (rank.get(b.summary_id) ?? 999999)
  );
}

function padClockPart(part) {
  return String(part).padStart(2, "0");
}

/** Stored clock as HH:MM:SS. Missing seconds become 00 so the box matches submit. */
export function formatActionedAtClock(timeString) {
  const clock = parseActionedAtClock(timeString);
  if (!clock) return null;
  return `${padClockPart(clock.hour)}:${padClockPart(clock.minute)}:${padClockPart(clock.second)}`;
}

/** Box default: stored clock, otherwise the UTC now captured when the popup opened. */
export function defaultPushToAuditTrailBoxTime(option, nowTime) {
  return formatActionedAtClock(option?.actioned_at_time) ?? nowTime;
}

export function buildPushToAuditTrailTimes(options, nowTime) {
  return (options ?? []).map((option) =>
    defaultPushToAuditTrailBoxTime(option, nowTime)
  );
}

/** Actioned at for option i is date + times[i], in popup row order. */
export function buildPushToAuditTrailRequest({ date, options, times }) {
  return {
    date,
    summary_ids: (options ?? []).map((option) => option.summary_id),
    times,
  };
}
